/* @ngInject */
function AuditSocialMediaAccountsController ($rootScope, $scope, $q, $translate, $timeout, Utils, UdsModalService, SocialMediaService, ToastService, UserService, ConfirmationModalService) {
  const ABBREVIATION_THRESHOLD = 10000
  const locale = $rootScope.oUser.locale
  const socialType = 'instagram'

  const getSocialMediaNonConformityController = () => $q.when(import('../../dialogs/social-media/non-conformity/non-conformity.controller'))

  $scope.flags = {
    isUserSysAdminAdminDesigner: $rootScope.isUserSysAdminAdminDesigner(),
    loading: false,
    loadMore: false,
    top: 10,
    skip: 0,
    $sort: 'lastPublishedDate',
    $order: 'desc',
    hasMoreResults: false
  }

  $scope.accounts = []
  $scope.totalAccounts = 0

  const api = {
    getSocialMediaNonConformityController,
    _getDateTimeFormat,
    _getAccounts,
    _checkSortOrder,
    _getAccountsLength,
    _mountColumns,
    _getTopAndSkip,
    _hasMoreResults,
    _mountAccounts
  }

  this.getApi = () => api

  activate()

  function activate () {}

  const deregisterFiltersWatcher = $scope.$watchCollection('filters', () => {
    api._getAccounts()
  })

  $scope.$on('$destroy', () => {
    deregisterFiltersWatcher()
  })

  $scope.sortColumnClick = (column) => {
    if (!column.isSortable) {
      return
    }

    $scope.flags.skip = 0

    if ($scope.flags.$sort === column.name) {
      api._checkSortOrder($scope.flags.$order)
    } else {
      $scope.flags.$sort = column.name
      $scope.flags.$order = 'asc'
    }

    api._getAccounts()
  }

  $scope.onReport = (account) => {
    api.getSocialMediaNonConformityController().then(({ SocialMediaNonConformityController }) => {
      const socialMediaNonConformityDialog = UdsModalService.createModal({
        controller: SocialMediaNonConformityController,
        modalName: 'socialMediaNonConformityDialog',
        data: {
          reportType: 'account',
          account: {
            id: account.id,
            name: account.name,
            username: account.username
          }
        }
      })
      socialMediaNonConformityDialog.open()
    })
  }

  $scope.formatLastPostDate = (lastPostDate) => {
    if (!lastPostDate) return '-'

    const formattedDate = api._getDateTimeFormat(lastPostDate, {
      month: 'short',
      day: 'numeric'
    }).replace('.', '')

    const formattedYear = api._getDateTimeFormat(lastPostDate, {
      year: 'numeric'
    })

    const formatted = `${formattedDate}, ${formattedYear}`

    return formatted
  }

  $scope.getSinceDate = (date) => {
    if (!date) return '-'

    return Utils.getTimeSince(date, locale)
  }

  $scope.getFormattedNumber = (number, simpleFormat) => {
    if (simpleFormat || number < ABBREVIATION_THRESHOLD) {
      return Utils.formatNumber(number, locale)
    }

    const options = {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1
    }

    return Utils.formatNumber(number, locale, options)
  }

  $scope.getColumnLabelColor = (column) => {
    const isSorted = $scope.flags.$sort === column.name && column.isSortable
    return isSorted ? 'neutralColorLowPure' : 'neutralColorLowMediumLightest'
  }

  $scope.getColumnIconColor = (column, order) => {
    const isSorted = $scope.flags.$sort === column.name && $scope.flags.$order === order && column.isSortable
    return isSorted ? 'neutralColorLowPure' : 'neutralColorLowMediumLightest'
  }

  $scope.handleLoadMore = () => {
    const loadMore = true
    api._getAccounts(loadMore)
  }

  $scope.getLastReportDateText = (lastReportDate) => {
    if (lastReportDate) {
      return $translate.instant('socialMedia.lastReportDate')
    }

    return '-'
  }

  $scope.unlinkAccount = (account) => {
    ConfirmationModalService.createModal({
      title: $translate.instant('socialMedia.unlinkAccount.title'),
      htmlDescription: $translate.instant('socialMedia.unlinkAccount.description'),
      buttonConfirmText: $translate.instant('socialMedia.unlinkAccount.buttonText'),
      onConfirm: () => {
        SocialMediaService.unlinkAccount(account.id).then(() => {
          $scope.accounts = $scope.accounts.filter(a => a.id !== account.id)
          $rootScope.$broadcast('socialMedia.removePosts', account.id)
          ToastService.showSuccessToast($translate.instant('socialMedia.unlinkAccount.success', { accountName: account.name }))
        }).catch(() => {
          ToastService.showErrorToast($translate.instant('socialMedia.unlinkAccount.error'))
        })
      }
    })
  }

  $scope.getAvatarUrl = (account) => {
    return UserService.getUserProfileImage(account.userId)
  }

  function _getDateTimeFormat (date, options) {
    return new Intl.DateTimeFormat(locale, options).format(new Date(date))
  }

  async function _getAccounts (loadMore = false) {
    if (!loadMore) {
      $scope.flags.loading = true
      $scope.flags.skip = 0
    }

    const filters = $scope.filters

    const { top, skip } = api._getTopAndSkip(loadMore)

    const params = {
      top,
      skip,
      $order: $scope.flags.$order,
      $sort: $scope.flags.$sort,
      accountIds: filters?.accounts,
      groupIds: filters?.groups
    }

    try {
      const response = await SocialMediaService.getAccounts(socialType, params)

      const rows = response.rows
      const count = response.count

      $scope.totalAccounts = count

      api._mountAccounts(loadMore, rows, count)

      api._mountColumns()
    } catch (err) {
      ToastService.defaultErrorToast()
      console.error(err)
    } finally {
      $timeout(() => {
        $scope.flags.loading = false
        $scope.flags.loadMore = false
      })
    }
  }

  function _checkSortOrder (orderScope) {
    const orders = {
      asc: () => {
        $scope.flags.$order = 'desc'
      },
      desc: () => {
        $scope.flags.$order = 'asc'
      }
    }

    orders[orderScope]()
  }

  function _getAccountsLength (length) {
    if (!length) return ''

    const formattedTotalLength = Utils.formatNumber(length, locale)

    return `(${formattedTotalLength})`
  }

  function _mountColumns () {
    $scope.columns = [
      {
        name: 'accounts',
        label: $translate.instant('socialMedia.accounts'),
        isSortable: false,
        count: api._getAccountsLength($scope.totalAccounts),
        alignment: 'left',
        class: ''
      },
      {
        name: 'postsCount',
        label: $translate.instant('socialMedia.posts'),
        isSortable: true,
        count: 0,
        alignment: 'center',
        class: ''
      },
      {
        name: 'followersCount',
        label: $translate.instant('socialMedia.followers'),
        isSortable: true,
        count: 0,
        alignment: 'center',
        class: ''
      },
      {
        name: 'followsCount',
        label: $translate.instant('socialMedia.following'),
        isSortable: true,
        count: 0,
        alignment: 'center',
        class: ''
      },
      {
        name: 'lastPublishedDate',
        label: $translate.instant('socialMedia.lastPost'),
        isSortable: true,
        count: 0,
        alignment: 'center',
        class: 'grow-2'
      },
      {
        name: 'nonConformity',
        label: $translate.instant('socialMedia.nonConformity'),
        isSortable: false,
        count: 0,
        alignment: 'left',
        class: 'grow-2'
      }
    ]
  }

  function _getTopAndSkip (loadMore) {
    if (loadMore) {
      $scope.flags.loadMore = true
      $scope.flags.skip = $scope.flags.top + $scope.flags.skip
    }

    return {
      top: $scope.flags.top,
      skip: $scope.flags.skip
    }
  }

  function _hasMoreResults (totalCount, responseLength) {
    if (!responseLength) {
      return false
    }

    return totalCount > responseLength
  }

  function _mountAccounts (loadMore, accounts, count) {
    $scope.accounts = loadMore ? [...$scope.accounts, ...accounts] : accounts

    const accountsLength = $scope.accounts.length
    $scope.flags.hasMoreResults = api._hasMoreResults(count, accountsLength)
  }
}

export { AuditSocialMediaAccountsController }
